import axios from 'axios';
import { ClerkTokenSingleton } from '~/services/token';

const TOUR_API_URL = process.env.TOUR_API_URL || 'http://localhost:3000';
const ANALYTICS_BASE_URL = process.env.ANALYTICS_PUBLIC_API_ENDPOINT || '';
const CLIENT_API_URL = process.env.CLIENT_API_URL || '';
const EXPLORER_API_URL = process.env.EXPLORER3D_API_URL;

export async function getAllCompaniesHostingStats(
  accessibleCompanies = [],
  propertiesAccessList = [],
) {
  const res = await fetch(
    `${CLIENT_API_URL}/stats/getAllCompaniesHostingStats`,
  );

  const body = await res.json();
  if (!res.ok || !body.result) {
    throw new Error('getAllCompaniesHostingStats request error');
  }

  const filteredCompanies =
    accessibleCompanies.length > 0
      ? body.result.filter(company =>
          accessibleCompanies.includes(company.company_name),
        )
      : body.result;

  const result =
    propertiesAccessList.length > 0
      ? filteredCompanies.map(company => ({
          ...company,
          properties: company.properties.filter(property =>
            propertiesAccessList.includes(property.property_id),
          ),
        }))
      : filteredCompanies;

  return result;
}

export async function getExplorer3dById(id) {
  const res = await fetch(`${EXPLORER_API_URL}/explorer3ds/units/all/${id}`);

  const body = await res.json();

  if (!res.ok) {
    throw new Error('getExplorer3dById request error');
  }

  return body;
}

export async function getExplorer3dUploadedContent(id) {
  const res = await fetch(
    `${EXPLORER_API_URL}/explorer3ds/${id}/uploaded-content`,
  );

  const body = await res.json();

  if (!res.ok) {
    throw new Error('getExplorer3dUploadedContent request error');
  }

  return body;
}

export async function getExplorer3dUnits(explorer3dId) {
  const res = await fetch(`${EXPLORER_API_URL}/units/${explorer3dId}`);
  const body = await res.json();

  if (!res.ok) {
    throw new Error('getExplorer3dUnits request error');
  }

  return body;
}

export async function getExplorer3dUnitTypes(explorer3dId) {
  const res = await fetch(`${EXPLORER_API_URL}/unit-types/${explorer3dId}`);
  const body = await res.json();

  if (!res.ok) {
    throw new Error('getExplorer3dUnitTypes request error');
  }

  return body;
}

export async function getExplorers3D() {
  const res = await fetch(
    `${EXPLORER_API_URL}/explorer3ds?offset=0&limit=10000000`,
  );

  const body = await res.json();
  if (!res.ok || !body.list) {
    throw new Error('getExplorer3dsByPropertyId request error');
  }

  return body.list;
}

export async function getCompanyHostingStats(companyId) {
  const res = await fetch(
    `${CLIENT_API_URL}/stats/getCompanyHostingStats?companyId=${companyId}`,
  );
  const body = await res.json();
  if (!res.ok || !body.result) {
    throw new Error('getCompanyHostingStats request error');
  }

  return body.result;
}

export async function getCompanyOverallStats(
  companyName = '',
  accessiblePropertiesIds = [],
  accessible3DExplorers = [],
) {
  const res = await fetch(`${ANALYTICS_BASE_URL}/getCompanyOverallStats`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ companyName, accessible3DExplorers }),
  });

  const body = await res.json();
  if (!res.ok || !body.result) {
    throw new Error('getCompanyOverallStats request error');
  }

  const filteredResult = body.result.filter(({ property_id }) =>
    accessiblePropertiesIds.includes(property_id),
  );

  return filteredResult;
}

export async function getAllCompaniesStats(
  accessibleCompanies = [],
  accessiblePropertiesIds = [],
  accessible3DExplorers = [],
) {
  const res = await fetch(`${ANALYTICS_BASE_URL}/getAllCompaniesStats`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ accessibleCompanies, accessible3DExplorers }),
  });

  const body = await res.json();
  if (!res.ok || !body.result) {
    throw new Error('getAllCompaniesStats request error');
  }

  const filteredResult =
    accessiblePropertiesIds.length > 0
      ? body.result.map(company => ({
          ...company,
          stats: company.stats.filter(stat =>
            accessiblePropertiesIds.includes(stat.property_id),
          ),
        }))
      : body.result;

  return filteredResult;
}

export async function getPropertyOverallStats(
  companyId,
  propertyId,
  explorerIds,
) {
  const res = await fetch(`${ANALYTICS_BASE_URL}/getPropertyOverallStats`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ companyId, propertyId, explorerIds }),
  });

  const body = await res.json();
  if (!res.ok || !body.result) {
    throw new Error('getPropertyOverallStats request error');
  }

  return body.result;
}

export async function get3DExplorerOverallStats(locationId) {
  const res = await fetch(
    `${ANALYTICS_BASE_URL}/get3DExplorerOverallStats?locationId=${locationId}`,
  );
  const body = await res.json();
  if (!res.ok || !body.result) {
    throw new Error('get3DExplorerOverallStats request error');
  }

  return body.result;
}

export async function getLocationOverallStats(locationId) {
  const res = await fetch(
    `${ANALYTICS_BASE_URL}/getLocationOverallStats?locationId=${locationId}`,
  );
  const body = await res.json();
  if (!res.ok || !body.result) {
    throw new Error('getLocationOverallStats request error');
  }

  return body.result;
}

export async function request({ method, url, body, params, onProgress }) {
  const u = new URL(url);
  const token = await ClerkTokenSingleton.I.getToken({ skipCache: true });
  let b = null;
  let headers = {};

  for (const [key, value] of Object.entries(params ?? {})) {
    u.searchParams.set(key, value.toString());
  }

  if (body instanceof FormData) {
    b = body;
  } else if (body) {
    headers = {
      accept: 'application/json',
      'content-type': 'application/json',
    };
    b = JSON.stringify(body);
  }

  const res = await axios(u.toString(), {
    method,
    data: b,
    onUploadProgress: p => {
      onProgress && onProgress(p.progress);
    },
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  });

  return res.data;
}

export function getAwsContentLinks(entityId, contentType) {
  return request({
    method: 'GET',
    url: `${EXPLORER_API_URL}/aws/get-links`,
    params: { entityId, contentType },
  });
}

export function listAwsFilesFolders(prefix) {
  return request({
    method: 'GET',
    url: `${EXPLORER_API_URL}/aws/list-files-folders`,
    params: { prefix },
  });
}

export async function downloadAwsFile(fileUrl) {
  const token = await ClerkTokenSingleton.I.getToken({ skipCache: true });
  const res = await axios.post(
    `${EXPLORER_API_URL}/aws/download`,
    { url: fileUrl },
    {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return res.data;
}

export async function downloadAwsContentArchive(body) {
  const token = await ClerkTokenSingleton.I.getToken({ skipCache: true });
  const res = await axios.post(
    `${EXPLORER_API_URL}/aws/download-content-archive`,
    body,
    {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return res.data;
}

export function uploadAwsFiles(files, bucketName, folderName, updatedBy) {
  const fd = new FormData();
  files.forEach(file => {
    fd.append('files', file);
  });
  fd.append('bucketName', bucketName);
  fd.append('folderName', folderName);
  if (updatedBy) fd.append('updatedBy', updatedBy);

  return request({
    method: 'POST',
    url: `${EXPLORER_API_URL}/aws/upload-files`,
    body: fd,
  });
}

export function uploadAwsVideos(files, bucketName, folderName, updatedBy) {
  const fd = new FormData();
  files.forEach(file => {
    fd.append('files', file);
  });
  fd.append('bucketName', bucketName);
  fd.append('folderName', folderName);
  if (updatedBy) fd.append('updatedBy', updatedBy);

  return request({
    method: 'POST',
    url: `${EXPLORER_API_URL}/aws/upload-videos`,
    body: fd,
  });
}

export function deleteAwsFile(fileUrl, entityType, updatedBy) {
  return request({
    method: 'POST',
    url: `${EXPLORER_API_URL}/aws/delete-file`,
    body: {
      fileUrl,
      entityType,
      ...(updatedBy ? { updatedBy } : {}),
    },
  });
}

export function deleteAws360Files(fileUrl, updatedBy) {
  return request({
    method: 'POST',
    url: `${EXPLORER_API_URL}/aws/delete-360-files`,
    body: {
      fileUrl,
      ...(updatedBy ? { updatedBy } : {}),
    },
  });
}

export function postAwsDragDrop({
  entityId,
  contentType,
  urls,
  isUnit,
  updatedBy,
}) {
  return request({
    method: 'POST',
    url: `${EXPLORER_API_URL}/aws/drag-drop`,
    body: {
      entityId,
      contentType,
      urls,
      isUnit,
      ...(updatedBy ? { updatedBy } : {}),
    },
  });
}

export function updateBulkExplorer3DUnits({ explorer3dId, units, hidePrices }) {
  return request({
    method: 'PATCH',
    url: `${EXPLORER_API_URL}/units/${explorer3dId}/update-bulk-units`,
    body: {
      explorer3dId,
      units,
      hidePrices,
    },
  });
}

export async function getLocationButtonClicksChartStats(
  locationId,
  dateStart = 0,
  dateEnd = Date.now(),
) {
  const menuItems = {
    0: 'Minimap',
    1: 'Fullscreen',
    2: 'Options toggle button',
    3: 'Share',
    4: 'VR',
    5: 'Screenshot',
    6: 'Measure',
    7: 'Dollhouse',
    8: 'Help',
    9: 'Settings',
    10: 'Map',
    11: 'Guided tour',
  };

  const menuColors = {
    0: '#ccc',
    1: '#9796F0',
    2: '#cc2964',
    3: '#51DCFF',
    4: '#630EEE',
    5: '#FFE259',
    6: '#fa2',
    7: '#29CC97',
    8: '#29a5cc',
    9: '#FF9212',
    10: '#0e6a07',
    11: '#87148f',
  };

  const res = await fetch(
    `${ANALYTICS_BASE_URL}/getLocationButtonClicksChartStats?locationId=${locationId}&dateStart=${dateStart}&dateEnd=${dateEnd}`,
  );

  if (!res.ok) {
    throw new Error('getLocationButtonClicksChartStats request error');
  }
  const { result } = await res.json();

  // return result.message.reduce((acc, {itemId, eventsCount}) => ({...acc, [menuItems[itemId]]: {count: eventsCount, color: menuColors[itemId]}}), {});
  return (
    result.message
      // .filter(({ itemId }) => ![0].includes(itemId))
      .map(({ itemId, eventsCount }) => ({
        name: menuItems[itemId],
        count: eventsCount,
        color: menuColors[itemId],
      }))
  );
}

export async function getAllCompaniesViewsChartStats(
  accessibleCompanies = [],
  accessibleProperties = [],
  accessible3DExplorers = [],
) {
  const now = Date.now();
  const lastYear = new Date(now);
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  const res = await fetch(
    `${ANALYTICS_BASE_URL}/getAllCompaniesViewsChartStats?dateStart=${lastYear.getTime()}&dateEnd=${now}`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accessibleCompanies,
        accessibleProperties,
        accessible3DExplorers,
      }),
    },
  );

  if (!res.ok) {
    throw new Error('getAllCompaniesViewsChartStats request error');
  }
  const { result } = await res.json();

  return result.message;
}

export async function getCompanyViewsChartStats(
  companyName = '',
  accessibleProperties = [],
  accessible3DExplorers = [],
) {
  const now = Date.now();
  const lastYear = new Date(now);
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  const res = await fetch(
    `${ANALYTICS_BASE_URL}/getCompanyViewsChartStats?dateStart=${lastYear.getTime()}&dateEnd=${now}`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        companyName,
        accessibleProperties,
        accessible3DExplorers,
      }),
    },
  );

  if (!res.ok) {
    throw new Error('getCompanyViewsChartStats request error');
  }
  const { result } = await res.json();

  return result.message;
}

export async function getPropertyViewsChartStats(propertyId, explorerIds) {
  const now = Date.now();
  const lastYear = new Date(now);
  lastYear.setFullYear(lastYear.getFullYear() - 1);
  const data = {
    propertyId: propertyId,
    dateStart: lastYear.getTime(),
    dateEnd: now,
    explorerIds: explorerIds,
  };

  const res = await fetch(`${ANALYTICS_BASE_URL}/getPropertyViewsChartStats`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    throw new Error('getPropertyViewsChartStats request error');
  }
  const { result } = await res.json();

  return result.message;
}

export async function getLocationViewsChartStats(locationId) {
  const now = Date.now();
  const lastYear = new Date(now);
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  const res = await fetch(
    `${ANALYTICS_BASE_URL}/getLocationViewsChartStats?locationId=${encodeURIComponent(
      locationId,
    )}&dateStart=${lastYear.getTime()}&dateEnd=${now}`,
  );

  if (!res.ok) {
    throw new Error('getLocationViewsChartStats request error');
  }
  const { result } = await res.json();

  return result.message;
}

export async function get3DExplorerLaunchesChartStats(locationId) {
  const now = Date.now();
  const lastYear = new Date(now);
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  const res = await fetch(
    `${ANALYTICS_BASE_URL}/get3DExplorerLaunchesChartStats?locationId=${encodeURIComponent(
      locationId,
    )}&dateStart=${lastYear.getTime()}&dateEnd=${now}`,
  );

  if (!res.ok) {
    throw new Error('get3DExplorerLaunchesChartStats request error');
  }
  const { result } = await res.json();

  return result.message;
}

export async function getLocationViewDurationChartStats(locationId) {
  const now = Date.now();
  const lastYear = new Date(now);
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  const res = await fetch(
    `${ANALYTICS_BASE_URL}/getLocationViewDurationChartStats?locationId=${encodeURIComponent(
      locationId,
    )}&dateStart=${lastYear.getTime()}&dateEnd=${now}`,
  );

  if (!res.ok) {
    throw new Error('getLocationViewDurationChartStats request error');
  }
  const { result } = await res.json();

  return result.message;
}

export async function getLocationViewDurationPointChartStats(locationId) {
  const res = await fetch(
    `${ANALYTICS_BASE_URL}/getLocationViewDurationPointChartStats?locationId=${encodeURIComponent(
      locationId,
    )}`,
  );

  if (!res.ok) {
    throw new Error('getLocationViewDurationPointChartStats request error');
  }
  const { result } = await res.json();

  return result.message;
}

export async function getLocationPointClicksChartStats(
  locationId,
  dateStart,
  dateEnd,
  getLocationPointClicksChartStats,
) {
  const now = Date.now();
  const res = await fetch(
    `${ANALYTICS_BASE_URL}/getLocationPointClicksChartStats?locationId=${locationId}&dateStart=${dateStart}&dateEnd=${dateEnd}`,
  );

  if (!res.ok) {
    throw new Error('getLocationPointClicksChartStats request error');
  }
  const { result } = await res.json();

  return result.message;
}

export async function getPropertyHeatmapStats(propertyId, explorerIds) {
  const res = await fetch(`${ANALYTICS_BASE_URL}/getPropertyHeatmapStats`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ propertyId, explorerIds }),
  });

  if (!res.ok) {
    throw new Error('getPropertyHeatmapStats request error');
  }
  const { result } = await res.json();

  return result;
}

export async function getLocationHeatmapStats(
  locationId,
  isExplorer3D = false,
) {
  const res = await fetch(
    `${ANALYTICS_BASE_URL}/getLocationHeatmapStats?locationId=${locationId}&isExplorer3D=${isExplorer3D}`,
  );

  if (!res.ok) {
    throw new Error('getLocationHeatmapStats request error');
  }
  const { result } = await res.json();

  return result;
}

export const existsOnServer = async url => {
  try {
    const res = await fetch(url, { method: 'GET', mode: 'cors' });
    return res.status === 200;
  } catch (e) {
    return false;
  }
};
